import { observable, action, computed, makeObservable, runInAction } from 'mobx'
import dayjs from 'dayjs'
import { Order } from '../Types/Order'
import { OrderItem } from '../Types/OrderItem'
import { FilterStatus } from '../Types/Enums'
import { FetchStatus, FetchResult } from '~shared/lib/Types/FetchTypes'
import { OfflineDevice } from '../Types/SiteHealth'
import { getApiErrorMessage } from '~shared/util/apiErrorMessage'
import {
  fetchOrders as fetchOrdersApi,
  fetchDepartmentAvailabilityStatus as fetchDepartmentAvailabilityStatusApi,
  updateDepartmentAvailabilityStatus as updateDepartmentAvailabilityStatusApi,
  editOrder as editOrderApi,
  cancelOrder as cancelOrderApi,
} from '../Services/OrderService'
import { fetchSiteHealth as fetchSiteHealthApi } from '../Services/DeviceService'
import type {
  DepartmentAvailabilityStatus,
  SetDepartmentAvailabilityRequest,
} from '../Types/DepartmentAvailability'
import KdsAuthorizationStore from './KdsAuthorizationStore'
import { KdsActionId } from '../Types/KdsAuthorization'
import { KdsAuthorizationCancelledError } from '../Types/Authorization'

class KdsStore {
  ordersFetch: FetchResult<Order[]> = {
    status: FetchStatus.unloaded,
    lastFetch: dayjs().format(),
    data: [],
  }

  departmentAvailable = false

  departmentStatus: DepartmentAvailabilityStatus = 'Online'

  additionalPrepMinutes = 0

  // Distinguishes the constructor defaults above from a value the API actually returned, so
  // consumers don't treat the initial optimistic 'Online' as authoritative before the first fetch.
  departmentAvailabilityLoaded = false

  // UTC instant a Busy adjustment auto-expires (the server flips the department back to Online).
  // Drives the scheduled refresh so the KDS stops showing Busy once it lapses.
  departmentAvailabilityExpiresAtUtc: string | null = null

  // Not observable: a plain handle used to cancel a pending Busy-expiry refresh.
  #expiryRefreshTimer: ReturnType<typeof setTimeout> | undefined

  // Order polling can overlap with manual refreshes, filter changes, and SignalR updates.
  // Keep only the newest request's result and cancel work that is still in flight.
  private ordersAbortController: AbortController | undefined
  private ordersRequestVersion = 0

  currentFilter = FilterStatus.Active

  unconfirmedActiveCount = 0

  unconfirmedUpcomingCount = 0

  newActiveCount = 0

  initialLoad = true

  siteHealthFetch: FetchResult<OfflineDevice[]> = {
    status: FetchStatus.unloaded,
    lastFetch: dayjs().format(),
    data: [],
  }

  constructor() {
    makeObservable(this, {
      ordersFetch: observable,
      currentFilter: observable,
      initialLoad: observable,
      departmentAvailable: observable,
      departmentStatus: observable,
      additionalPrepMinutes: observable,
      departmentAvailabilityLoaded: observable,
      departmentAvailabilityExpiresAtUtc: observable,
      unconfirmedActiveCount: observable,
      unconfirmedUpcomingCount: observable,
      newActiveCount: observable,
      siteHealthFetch: observable,
      fetchOrders: action.bound,
      editOrderStatus: action.bound,
      setFilter: action.bound,
      updateOrder: action.bound,
      filteredOrders: computed,
      hasNewOrders: computed,
      offlineDeviceCount: computed,
      numberOfActiveOrdersRequiringConfirmation: computed,
      numberOfUpcomingOrdersRequiringConfirmation: computed,
      updateDepartmentAvailabilityStatus: action.bound,
      fetchDepartmentAvailability: action.bound,
      fetchSiteHealth: action.bound,
      cancelCustomerOrder: action.bound,
      authorizeOrderEntry: action.bound,
    })
  }

  async fetchOrders() {
    this.ordersAbortController?.abort()
    const abortController = new AbortController()
    this.ordersAbortController = abortController
    const requestVersion = ++this.ordersRequestVersion

    this.ordersFetch = {
      ...this.ordersFetch,
      status: FetchStatus.fetching,
      lastFetch: dayjs().format(),
    }

    try {
      const statusSegment = this.currentFilter.toLowerCase()
      const data = await fetchOrdersApi(statusSegment, abortController.signal)

      if (abortController.signal.aborted || requestVersion !== this.ordersRequestVersion) {
        return
      }

      runInAction(() => {
        this.ordersFetch = {
          status: FetchStatus.success,
          lastFetch: dayjs().format(),
          data: data.orders,
          error: undefined,
        }

        this.unconfirmedActiveCount = data.unconfirmedActiveCount || 0
        this.unconfirmedUpcomingCount = data.unconfirmedUpcomingCount || 0
        this.newActiveCount = data.newActiveCount || 0

        if (this.initialLoad) {
          this.initialLoad = false
        }
      })
    } catch (error) {
      if (abortController.signal.aborted || requestVersion !== this.ordersRequestVersion) {
        return
      }

      console.error('Failed to fetch orders:', error)
      runInAction(() => {
        this.ordersFetch = {
          ...this.ordersFetch,
          status: FetchStatus.error,
          error: getApiErrorMessage(error),
        }

        if (this.initialLoad) {
          this.initialLoad = false
        }
      })
    } finally {
      if (this.ordersAbortController === abortController) {
        this.ordersAbortController = undefined
      }
    }
  }

  fetchDepartmentAvailability = async () => {
    try {
      const data = await fetchDepartmentAvailabilityStatusApi()

      runInAction(() => {
        this.applyAvailability(
          data.status,
          data.additionalPrepMinutes ?? 0,
          data.expiresAtUtc ?? null
        )
      })
    } catch (error) {
      console.error('Failed to fetch department availability:', error)
    }
  }

  // Writes the resolved availability into the observable state and (re)arms the expiry refresh.
  // Must run inside an action -- callers wrap it in runInAction.
  private applyAvailability(
    status: DepartmentAvailabilityStatus,
    additionalPrepMinutes: number,
    expiresAtUtc: string | null
  ) {
    this.departmentStatus = status
    this.additionalPrepMinutes = additionalPrepMinutes
    this.departmentAvailable = status !== 'Offline'
    this.departmentAvailabilityExpiresAtUtc = expiresAtUtc
    this.departmentAvailabilityLoaded = true
    this.scheduleExpiryRefresh(status, expiresAtUtc)
  }

  // A Busy adjustment lapses server-side at expiresAtUtc, flipping the department back to Online.
  // Re-fetch just past that boundary so the KDS reflects it without waiting for the next manual
  // refresh. Any non-Busy / no-expiry state just cancels a pending timer.
  private scheduleExpiryRefresh(status: DepartmentAvailabilityStatus, expiresAtUtc: string | null) {
    if (this.#expiryRefreshTimer) {
      clearTimeout(this.#expiryRefreshTimer)
      this.#expiryRefreshTimer = undefined
    }

    if (status !== 'Busy' || !expiresAtUtc) {
      return
    }

    // +1s past the boundary so the server has actually flipped the state before we re-read it.
    const delay = Math.max(0, dayjs(expiresAtUtc).diff(dayjs())) + 1000
    this.#expiryRefreshTimer = setTimeout(() => {
      this.#expiryRefreshTimer = undefined
      void this.fetchDepartmentAvailability()
    }, delay)
  }

  /**
   * Updates department availability (tri-state: Online / Busy+prep-time / Offline) through the
   * shared authorization flow. When the config marks `SetDepartmentAvailability` as elevated the
   * operator is prompted for a PIN and the change is submitted with the step-up JWT; otherwise it
   * runs on the device token exactly as before. Returns true when availability changed, false when
   * the operator dismissed the prompt. Throws on a genuine failure (e.g. step-up 401/403).
   */
  async updateDepartmentAvailabilityStatus(
    request: SetDepartmentAvailabilityRequest
  ): Promise<boolean> {
    try {
      // A Busy write changes availability AND sets prep time, so the endpoint requires both the
      // SetDepartmentAvailability policy and the AdjustDepartmentPrepTime permission. Require both
      // actions so a PIN step-up is prompted whenever either is Force-PIN'd (a single step-up token
      // carries both). Online/Offline only need SetDepartmentAvailability.
      const requiredActions =
        request.status === 'Busy'
          ? [KdsActionId.SetDepartmentAvailability, KdsActionId.AdjustDepartmentPrepTime]
          : [KdsActionId.SetDepartmentAvailability]

      await KdsAuthorizationStore.runConfiguredActions(
        requiredActions,
        () => updateDepartmentAvailabilityStatusApi(request),
        authorization => updateDepartmentAvailabilityStatusApi(request, authorization.token)
      )
      // The POST succeeded, so reflect the requested state immediately. The reconciling fetch
      // below swallows its own errors, so without this a failed refresh would leave the store
      // showing the old status even though the change took. Expiry is left null until the fetch
      // returns the authoritative value.
      runInAction(() => {
        this.applyAvailability(request.status, request.additionalPrepMinutes ?? 0, null)
      })
      await this.fetchDepartmentAvailability()
      return true
    } catch (error) {
      if (error instanceof KdsAuthorizationCancelledError) {
        return false
      }
      console.error('Failed to update department availability:', error)
      throw error
    }
  }

  async editOrderStatus(orderId: string, newStatus: string): Promise<void> {
    await editOrderApi(orderId, { status: newStatus })

    await this.fetchOrders()
  }

  /**
   * Centralized method to handle all order updates including status changes and weight confirmations
   *
   * @param orderId - The ID of the order to update
   * @param updatedItems - The updated items (empty array for status-only changes)
   * @param targetStatus - The target status to set for the order (null if no status change)
   * @returns {Promise<void>}
   */
  async updateOrder(
    orderId: string,
    updatedItems: OrderItem[],
    targetStatus: string | null
  ): Promise<void> {
    const order = this.ordersFetch.data?.find(o => o.id === orderId)

    if (!order) {
      throw new Error('Order not found')
    }

    await editOrderApi(orderId, { status: targetStatus, items: updatedItems })

    await this.fetchOrders()
  }

  /**
   * Cancels via the shared step-up flow: a PIN when the config marks the action elevated, the
   * device token otherwise. Returns false when the operator dismisses the prompt; throws on a
   * genuine failure (e.g. step-up 401/403).
   */
  async cancelCustomerOrder(orderUid: string): Promise<boolean> {
    try {
      await KdsAuthorizationStore.runConfiguredAction(
        KdsActionId.CancelCustomerOrders,
        () => cancelOrderApi(orderUid),
        authorization => cancelOrderApi(orderUid, authorization.token)
      )
      return true
    } catch (error) {
      if (error instanceof KdsAuthorizationCancelledError) {
        return false
      }
      throw error
    }
  }

  async authorizeOrderEntry(): Promise<{ stepUpToken?: string } | null> {
    try {
      const authorization = await KdsAuthorizationStore.authorizeConfiguredAction(
        KdsActionId.AddKdsOrders
      )
      return { stepUpToken: authorization?.token }
    } catch (error) {
      if (error instanceof KdsAuthorizationCancelledError) {
        return null
      }
      throw error
    }
  }

  setFilter(filter: FilterStatus) {
    this.currentFilter = filter
  }

  async fetchSiteHealth(): Promise<void> {
    this.siteHealthFetch = {
      ...this.siteHealthFetch,
      status: FetchStatus.fetching,
      lastFetch: dayjs().format(),
    }

    try {
      const data = await fetchSiteHealthApi()

      runInAction(() => {
        this.siteHealthFetch = {
          status: FetchStatus.success,
          lastFetch: dayjs().format(),
          data: data?.offlineDevices ?? [],
        }
      })
    } catch (error) {
      console.error('Failed to fetch site health:', error)
      runInAction(() => {
        this.siteHealthFetch = {
          ...this.siteHealthFetch,
          status: FetchStatus.error,
        }
      })
    }
  }

  get offlineDeviceCount(): number {
    return this.siteHealthFetch.data?.length ?? 0
  }

  get filteredOrders(): Order[] {
    const orders = this.ordersFetch.data ?? []
    return orders
  }

  get hasNewOrders(): boolean {
    return this.newActiveCount > 0
  }

  get numberOfActiveOrdersRequiringConfirmation(): number {
    return this.unconfirmedActiveCount
  }

  get numberOfUpcomingOrdersRequiringConfirmation(): number {
    return this.unconfirmedUpcomingCount
  }
}

const kdsStore = new KdsStore()
export default kdsStore
export { KdsStore }
